/**
 * Powerhouse (§7.1, view 3): unit cards, the hill chart with live operating points, the start
 * sequence checklist, rough-zone and bearing trends, trash-rack differential and the rake.
 */

import { useSession } from '../../store/session';
import { useDispatch } from '../useEngine';
import { getSite } from '../../sites';
import { HillChart } from '../charts/HillChart';
import { Trend } from '../charts/Trend';
import { Readout } from '../widgets/Readout';
import { useConfirmed } from '../widgets/Confirm';
import { Gloss } from '../glossary/GlossaryDrawer';

export function PowerhouseView(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const site = getSite(useSession((s) => s.siteId));
  const selected = useSession((s) => s.selectedUnit);
  const selectUnit = useSession((s) => s.selectUnit);
  const dispatch = useDispatch();
  const confirmed = useConfirmed();

  if (!snap) return <div style={{ padding: 24 }}>—</div>;
  const ph = site.powerhouse;
  const u = snap.units[selected];

  return (
    <div style={{ padding: 'var(--s3)', display: 'grid', gap: 'var(--s3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 'var(--s2)' }}>
        {snap.units.map((unit, i) => {
          const online = unit.mode === 'online';
          const loadFrac = unit.mw / ph.ratedMW;
          const rough = online && loadFrac >= ph.roughZoneLo && loadFrac < ph.roughZoneHi;
          const border =
            unit.mode === 'tripped' ? 'var(--alarm)' : rough ? 'var(--warn)' : selected === i ? 'var(--ink-dim)' : 'var(--line)';
          return (
            <button
              key={unit.id}
              onClick={() => selectUnit(i)}
              className="panel"
              style={{ textAlign: 'left', borderColor: border, background: 'var(--surface-1)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span className="title" style={{ fontSize: 16 }} role="heading" aria-level={2}>
                  Unit {i + 1}
                </span>
                <span
                  className="num"
                  style={{
                    textTransform: 'uppercase',
                    fontSize: 11,
                    color: unit.mode === 'tripped' ? 'var(--alarm)' : online ? 'var(--ok)' : 'var(--ink-dim)',
                  }}
                >
                  {unit.mode}
                  {unit.mode === 'starting' && ` · ${Math.ceil(unit.phaseS)} s`}
                </span>
              </div>
              <div className="readout" style={{ marginTop: 4 }}>
                {Math.round(unit.mw)}
                <span className="unit">MW</span>
              </div>
              <div className="num" style={{ color: 'var(--ink-dim)', fontSize: 12 }}>
                {Math.round(unit.q)} m³/s · {(unit.wicket * 100).toFixed(0)}% gate · η{' '}
                {(unit.efficiency * 100).toFixed(1)}% · {unit.netHead.toFixed(1)} m net
              </div>
              <div className="num" style={{ color: 'var(--ink-dim)', fontSize: 12 }}>
                bearing {unit.bearingTempC.toFixed(0)} °C · rack Δ{unit.rackDp.toFixed(2)} m ·{' '}
                {unit.starts} starts
              </div>
              {rough && (
                <div style={{ color: 'var(--warn)', fontSize: 12, marginTop: 4 }}>
                  ▲ <Gloss id="rough-zone">rough zone</Gloss> {unit.roughMinutes.toFixed(0)} min
                </div>
              )}
              {unit.tripCause && (
                <div style={{ color: 'var(--alarm)', fontSize: 12, marginTop: 4 }}>▲ {unit.tripCause}</div>
              )}
              {unit.derate < 0.999 && (
                <div style={{ color: 'var(--warn)', fontSize: 12, marginTop: 4 }}>
                  ▲ derated to {(unit.derate * 100).toFixed(0)}%
                </div>
              )}
            </button>
          );
        })}
      </div>

      {u && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 'var(--s3)' }}>
          <section className="panel">
            <div className="label" style={{ marginBottom: 8 }}>
              Unit {selected + 1} control
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s2)', marginBottom: 10 }}>
              <Readout label="Setpoint" value={Math.round(u.setpointMW).toString()} unit="MW" size="sm" />
              <Readout label="Speed" value={u.rpm.toFixed(0)} unit="rpm" size="sm" />
            </div>
            <label>
              <div className="label">MW setpoint</div>
              <input
                type="range"
                min={0}
                max={ph.ratedMW}
                step={1}
                value={Math.round(u.setpointMW)}
                disabled={u.mode !== 'online'}
                onChange={(e) => dispatch({ t: 'unit-setpoint', unit: selected, mw: Number(e.target.value) })}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ink-dim)', fontSize: 11 }} className="num">
                <span>0</span>
                <span style={{ color: 'var(--warn)' }}>
                  rough {ph.roughZoneLo * ph.ratedMW}–{ph.roughZoneHi * ph.ratedMW}
                </span>
                <span>min load {ph.minLoadFrac * ph.ratedMW}</span>
                <span>{ph.ratedMW}</span>
              </div>
            </label>

            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              {u.mode === 'offline' && (
                <button
                  className="primary"
                  onClick={() =>
                    confirmed(
                      `Start unit ${selected + 1}`,
                      `Intake gate ${ph.intakeGateSeconds} s → speed no load → sync ${ph.syncSeconds} s. $${ph.startCost.toLocaleString('en-US')} per start.`,
                      () => dispatch({ t: 'unit-start', unit: selected }),
                    )
                  }
                >
                  Start
                </button>
              )}
              {u.mode === 'online' && (
                <button
                  onClick={() =>
                    confirmed(`Stop unit ${selected + 1}`, 'The unit will unload and come off line.', () =>
                      dispatch({ t: 'unit-stop', unit: selected }),
                    )
                  }
                >
                  Stop
                </button>
              )}
              <button
                onClick={() => dispatch({ t: 'unit-rake', unit: selected })}
                disabled={u.rakingMinutes > 0}
                title={`Clears the trash racks over ${ph.rakeMinutes} minutes.`}
              >
                {u.rakingMinutes > 0 ? `Raking… ${Math.ceil(u.rakingMinutes)} min` : 'Rake racks'}
              </button>
              {u.mode === 'maintenance' && (
                <button
                  className="danger"
                  onClick={() =>
                    confirmed(
                      `Defer unit ${selected + 1} maintenance`,
                      'Returns the machine to service now, at a cost of $40,000.',
                      () => dispatch({ t: 'defer-maintenance', unit: selected }),
                    )
                  }
                >
                  Defer maintenance
                </button>
              )}
            </div>

            <div className="label" style={{ marginTop: 14, marginBottom: 4 }}>
              Start sequence
            </div>
            <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--ink-dim)', fontSize: 12 }}>
              {['Intake gate open', 'Wicket gates to speed no load', 'Synchronise', 'Loadable'].map((stepLabel, k) => {
                const done =
                  u.mode === 'online' ? true : u.mode === 'starting' ? u.startPhase >= k : false;
                const active = u.mode === 'starting' && u.startPhase === k;
                return (
                  <li key={stepLabel} style={{ color: active ? 'var(--ink)' : done ? 'var(--ok)' : 'var(--ink-dim)' }}>
                    {done && !active ? '✓ ' : active ? '▸ ' : '· '}
                    {stepLabel}
                  </li>
                );
              })}
            </ol>
          </section>

          <section className="panel">
            <div className="label" style={{ marginBottom: 6 }}>
              Hill chart — live operating points
            </div>
            <HillChart site={site} snap={snap} selected={selected} />
            <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 4 }}>
              Best efficiency sits at q = {site.powerhouse.efficiency.kind === 'parabolic' ? site.powerhouse.efficiency.qBep : '0.5–1.0'} —
              below rated flow, which is why the rated point makes {Math.round((1000 * 9.81 * ph.ratedFlow * ph.ratedHeadM * 0.927625 * ph.genEfficiency) / 1e6)} MW
              rather than the {ph.ratedMW} MW nameplate.
            </div>
          </section>

          <section className="panel">
            <div className="label" style={{ marginBottom: 6 }}>
              Generation and trash-rack differential
            </div>
            <Trend
              height={150}
              yLabel="MW"
              series={[{ x: snap.history.minuteT, y: snap.history.generation, colour: 'var(--water-shallow)', width: 1.8, label: 'export' }]}
              thresholds={[{ value: snap.exportLimitMW, colour: 'var(--warn)', label: 'export limit' }]}
            />
            <div className="num" style={{ marginTop: 8, color: 'var(--ink-dim)', fontSize: 12 }}>
              Rack differential derates Q_max above {ph.rackDpDerateThreshold.toFixed(1)} m. Worst
              unit now: {snap.rackDp.toFixed(2)} m. Intake water {snap.intakeTempC.toFixed(1)} °C.
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
